import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Modal } from "../components/Modal.tsx";
import {
	COURT,
	EXHIBITION_GAME_SETTINGS,
	PHASE,
	REAL_PLAYERS_INFO,
} from "../../common/constants.ts";
import { defaultGameAttributes } from "../../common/defaultGameAttributes.ts";
import type {
	GameAttributesLeague,
	Player,
	RealTeamInfo,
	Team,
	View,
} from "../../common/types.ts";
import { ActionButton } from "../components/ActionButton.tsx";
import { PlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { processPlayerStats } from "../util/processPlayerStats.ts";
import { safeLocalStorage } from "../util/safeLocalStorage.ts";
import { toWorker } from "../util/toWorker.ts";
import { applyRealTeamInfos } from "./NewLeague/index.tsx";
import SettingsForm from "./Settings/SettingsForm.tsx";
import { orderBy, range } from "../../common/utils.ts";
import { choice, randInt } from "../../common/random.ts";

export type ExhibitionTeam = {
	season: number;
	seasonInfo?: {
		won: number;
		lost: number;
		tied: number;
		otl: number;
		roundsWonText?: string;
	};
	players: Player[];
	ovr: number;
} & Pick<
	Team,
	| "abbrev"
	| "imgURL"
	| "imgURLSmall"
	| "region"
	| "name"
	| "tid"
	| "cid"
	| "depth"
	| "colors"
	| "jersey"
	| "stadiumCapacity"
>;

type ExhibitionLeague = {
	lid: number;
	name: string;
};

const playerRowClassName = (i: number) => {
	if (i === 0) {
		return;
	}

	if (i < 5) {
		return "mt-1";
	}

	return "mt-1 d-none d-sm-block";
};

export type ExhibitionGameAttributes = Pick<
	GameAttributesLeague,
	(typeof EXHIBITION_GAME_SETTINGS)[number]
>;

const getGameAttributes = (gameAttributes?: Partial<GameAttributesLeague>) => {
	const output: ExhibitionGameAttributes = {} as any;
	for (const key of EXHIBITION_GAME_SETTINGS) {
		if (gameAttributes?.[key] !== undefined) {
			(output as any)[key] = gameAttributes[key];
		} else {
			(output as any)[key] = defaultGameAttributes[key];
		}
	}

	return output;
};

export type ExhibitionLeagueWithSeasons =
	| {
			type: "real";
			seasonStart: number;
			seasonEnd: number;
	  }
	| {
			type: "league";
			lid: number;
			seasonStart: number;
			seasonEnd: number;
	  };

const PlayerStatsSummary = ({ stats }: { stats: Player["stats"][number] }) => {
	if (!stats || stats.gp === undefined || stats.gp === 0) {
		return <br />;
	}

	if (__SPORT === "basketball") {
		return (
			<>
				{helpers.roundStat(stats.pts / stats.gp, "pts")} pts /{" "}
				{helpers.roundStat(
					((stats.trb ?? 0) + (stats.drb ?? 0) + (stats.orb ?? 0)) / stats.gp,
					"trb",
				)}{" "}
				trb / {helpers.roundStat(stats.ast / stats.gp, "ast")} ast
			</>
		);
	}

	const processed = processPlayerStats(stats, ["keyStats"]);
	return processed.keyStats;
};

const SelectTeam = ({
	disabled,
	index,
	initialTeam,
	leagues,
	onChange,
	realTeamInfo,
	customPlayers,
	onOpenDraft,
	onReset,
	customTeamName,
}: {
	disabled: boolean;
	index: number;
	initialTeam?: CachedTeam;
	leagues: ExhibitionLeague[];
	onChange: (
		league: ExhibitionLeagueWithSeasons,
		t: ExhibitionTeam | undefined,
		gameAttributes: ExhibitionGameAttributes,
	) => void;
	realTeamInfo: RealTeamInfo | undefined;
	customPlayers?: Player[];
	onOpenDraft?: () => void;
	onReset?: () => void;
	customTeamName?: string;
}) => {
	const [league, setLeague] = useState<
		ExhibitionLeagueWithSeasons | undefined
	>();
	const [season, setSeason] = useState(0);
	const [loadingTeams, setLoadingTeams] = useState(true);
	const [tid, setTid] = useState(0);
	const [teams, setTeams] = useState<ExhibitionTeam[]>([]);
	const [gameAttributes, setGameAttributes] =
		useState<ExhibitionGameAttributes>(getGameAttributes);

	const loadLeague = async (lid: "real" | number) => {
		let newLeague: typeof league;
		if (lid === "real") {
			newLeague = {
				type: "real",
				seasonStart: REAL_PLAYERS_INFO?.MIN_SEASON ?? 0,
				seasonEnd: REAL_PLAYERS_INFO?.MAX_SEASON ?? 0,
			};
		} else {
			const { seasonStart, seasonEnd } = await toWorker(
				"exhibitionGame",
				"getSeasons",
				lid,
			);
			newLeague = {
				type: "league",
				lid,
				seasonStart,
				seasonEnd,
			};
		}

		setLeague(newLeague);

		return newLeague;
	};

	const loadTeams = async (
		league: ExhibitionLeagueWithSeasons,
		season: number,
		tidInput?: number | "random",
	) => {
		setLoadingTeams(true);
		onChange(league, undefined, getGameAttributes());

		const pidOffset = index * 1e6;

		const newInfo = await toWorker(
			"exhibitionGame",
			"getSeasonInfo",
			league.type === "real"
				? {
						type: "real",
						season,
						pidOffset,
					}
				: {
						type: "league",
						lid: league.lid,
						season,
						pidOffset,
					},
		);
		const newTeams = orderBy(
			applyRealTeamInfos(newInfo.teams, realTeamInfo, season),
			["region", "name", "tid"],
		);

		const prevTeam = teams.find((t) => t.tid === tid);
		let newTeam;
		if (tidInput === "random") {
			newTeam = choice(newTeams);
		} else {
			if (typeof tidInput === "number") {
				newTeam = newTeams.find((t) => t.tid === tidInput);
			}
			if (!newTeam) {
				newTeam =
					newTeams.find((t) => t.abbrev === prevTeam?.abbrev) ??
					newTeams.find((t) => t.region === prevTeam?.region) ??
					newTeams[0]!;
			}
		}

		const newGameAttributes = getGameAttributes(newInfo.gameAttributes);

		setTeams(newTeams as any);
		setTid(newTeam.tid);
		setGameAttributes(newGameAttributes);
		setLoadingTeams(false);

		onChange(league, newTeam as any, newGameAttributes);
	};

	const awaitingInitialLoad = useRef(true);
	useLayoutEffect(() => {
		const run = async () => {
			// We only want to do this once, on initial load ideally, but we may have to wait for leagues to be provided
			if (
				!awaitingInitialLoad.current ||
				(!REAL_PLAYERS_INFO && leagues.length === 0)
			) {
				return;
			}
			awaitingInitialLoad.current = false;

			try {
				if (initialTeam) {
					const lid = initialTeam.type === "real" ? "real" : initialTeam.lid;
					const league = await loadLeague(lid);
					setSeason(initialTeam.season);
					await loadTeams(league, initialTeam.season, initialTeam.tid);
					return;
				}
			} catch (error) {
				console.error(error);
			}

			const league = await loadLeague(
				REAL_PLAYERS_INFO ? "real" : leagues[0]!.lid,
			);
			const randomSeason = randInt(league.seasonStart, league.seasonEnd);
			setSeason(randomSeason);
			await loadTeams(league, randomSeason, "random");
		};

		run();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leagues]);

	const t = teams.find((t) => t.tid === tid);
	const activePlayers =
		customPlayers !== undefined ? customPlayers : (t?.players ?? []);
	let record;
	if (t?.seasonInfo && customPlayers === undefined) {
		record = helpers.formatRecord(t.seasonInfo);
	}

	const NUM_PLAYERS_TO_SHOW = 9;
	const playersToShow = activePlayers.slice(0, NUM_PLAYERS_TO_SHOW);

	return (
		<>
			<form>
				<div className="mb-2">
					<select
						className="form-select"
						value={
							!league ? undefined : league.type === "real" ? "real" : league.lid
						}
						disabled={disabled || !league}
						onChange={async (event) => {
							const value = event.target.value;
							const lid = value === "real" ? value : Number.parseInt(value);
							const league = await loadLeague(lid);
							setSeason(league.seasonEnd);
							await loadTeams(league, league.seasonEnd);
						}}
					>
						{REAL_PLAYERS_INFO ? (
							<option value="real">Real historical teams</option>
						) : null}
						{leagues.map((league) => (
							<option key={league.lid} value={league.lid}>
								{league.name}
							</option>
						))}
					</select>
				</div>
				<div className="input-group">
					<select
						className="form-select"
						value={season}
						onChange={async (event) => {
							const value = Number.parseInt(event.target.value);
							setSeason(value);
							await loadTeams(league!, value);
						}}
						disabled={disabled || !league}
						style={{
							maxWidth: 75,
						}}
					>
						{league
							? range(league.seasonEnd, league.seasonStart - 1).map((i) => (
									<option key={i} value={i}>
										{i}
									</option>
								))
							: null}
					</select>
					<select
						className="form-select"
						value={teams.length === 0 ? "loading" : tid}
						onChange={(event) => {
							const newTid = Number.parseInt(event.target.value);
							setTid(newTid);
							const newTeam = teams.find((t) => t.tid === newTid);
							onChange(league!, newTeam as any, gameAttributes);
						}}
						disabled={loadingTeams || disabled || !league}
					>
						{teams.map((t) => (
							<option key={t.tid} value={t.tid}>
								{t.region} {t.name} ({t.ovr} ovr)
							</option>
						))}
						{teams.length === 0 ? (
							<option value="loading">Loading...</option>
						) : null}
					</select>
					<button
						className="btn btn-light-bordered"
						type="button"
						disabled={disabled || !league}
						onClick={async () => {
							const randomSeason = randInt(
								league!.seasonStart,
								league!.seasonEnd,
							);
							setSeason(randomSeason);
							await loadTeams(league!, randomSeason, "random");
						}}
					>
						Random
					</button>
				</div>
			</form>

			<div className="d-flex gap-2 my-2">
				<button
					className="btn btn-sm btn-light-bordered flex-grow-1"
					type="button"
					disabled={disabled}
					onClick={onOpenDraft}
				>
					Draft / Edit Roster
				</button>
				{customPlayers !== undefined ? (
					<button
						className="btn btn-sm btn-outline-danger"
						type="button"
						disabled={disabled}
						onClick={onReset}
					>
						Reset
					</button>
				) : null}
			</div>

			{customPlayers !== undefined ? (
				<div
					className="alert alert-info py-1 px-2 mb-2 text-center"
					style={{ fontSize: "0.85rem" }}
				>
					Drafted Custom Roster ({customPlayers.length} player
					{customPlayers.length === 1 ? "" : "s"})
				</div>
			) : null}

			<div className="d-flex my-2">
				<div
					style={{ width: 128, height: 128 }}
					className="d-flex align-items-center justify-content-center"
				>
					{t?.imgURL ? (
						<img className="mw-100 mh-100" src={t.imgURL} alt="Team logo" />
					) : null}
				</div>
				{t ? (
					<div className="ms-2" style={{ marginTop: 22 }}>
						<h2 className="mb-0">
							{customPlayers !== undefined && customTeamName
								? customTeamName
								: `${t.region} ${t.name}`}
						</h2>
						<h2>{t.ovr} ovr</h2>
						{t.seasonInfo ? (
							<>
								<h2 className="mb-0">{record}</h2>
								{t.seasonInfo.roundsWonText !== undefined
									? helpers.upperCaseFirstLetter(t.seasonInfo.roundsWonText)
									: null}
							</>
						) : null}
					</div>
				) : null}
			</div>
			<ul className="list-unstyled mb-0">
				{playersToShow.map((p, i) => {
					const stats = p.stats.at(-1);
					const ratings = p.ratings.at(-1);

					return (
						<li key={p.pid} className={playerRowClassName(i)}>
							<PlayerNameLabels
								pid={p.pid}
								firstName={
									customPlayers !== undefined && ratings?.season
										? `${ratings.season} ${p.firstName}`
										: p.firstName
								}
								lastName={p.lastName}
								jerseyNumber={p.stats.at(-1)?.jerseyNumber ?? p.jerseyNumber}
								skills={p.ratings.at(-1)!.skills}
								fullNames
								disableNameLink
								season={season}
							/>{" "}
							<span className="text-body-secondary">-</span> {ratings.pos}{" "}
							<span className="text-body-secondary">-</span> {ratings.ovr} ovr
							<div className="exhibition-stats text-body-secondary">
								<PlayerStatsSummary stats={stats} />
							</div>
						</li>
					);
				})}
				{range(NUM_PLAYERS_TO_SHOW - playersToShow.length).map((j) => {
					const i = playersToShow.length + j;
					return (
						<li key={i} className={playerRowClassName(i)}>
							<br />
							<br />
						</li>
					);
				})}
			</ul>
		</>
	);
};

type ExhibitionTeamAndSettings = {
	gameAttributes: ExhibitionGameAttributes;
	league: ExhibitionLeagueWithSeasons;
	t: ExhibitionTeam;
};

const CACHE_KEY = "lastExhibitionGame";

type GameAttributesInfo =
	| {
			type: "t0" | "t1" | "default";
	  }
	| {
			type: "custom";
			custom: ExhibitionGameAttributes;
	  };

type CachedTeam =
	| {
			type: "real";
			season: number;
			tid: number;
	  }
	| {
			type: "league";
			lid: number;
			season: number;
			tid: number;
	  };
type CachedSettings = {
	gameAttributesInfo: GameAttributesInfo;
	neutralSite: boolean;
	playoffIntensity: boolean;
	swapHomeAway?: boolean;
	teams: [CachedTeam, CachedTeam];
	customPlayers1?: Player[];
	customPlayers2?: Player[];
	customTeamName1?: string;
	customTeamName2?: string;
};

const useLeagues = () => {
	const [leagues, setLeagues] = useState<ExhibitionLeague[] | undefined>();

	useLayoutEffect(() => {
		const run = async () => {
			const newLeagues = await toWorker(
				"exhibitionGame",
				"getLeagues",
				undefined,
			);
			setLeagues(newLeagues);
		};

		run();
	}, []);

	return leagues;
};

const RosterDraftModal = ({
	show,
	onHide,
	leagues,
	realTeamInfo,
	onAddPlayer,
	customPlayers1,
	customPlayers2,
	onRemovePlayer,
	onClearRoster,
	onChangePlayerSeason,
	defaultLeague,
	customTeamName1,
	customTeamName2,
	onChangeTeamName,
	onSetCustomPlayers1,
	onSetCustomPlayers2,
}: {
	show: boolean;
	onHide: () => void;
	leagues: ExhibitionLeague[];
	realTeamInfo: RealTeamInfo | undefined;
	onAddPlayer: (index: 0 | 1, player: Player) => void;
	customPlayers1: Player[] | undefined;
	customPlayers2: Player[] | undefined;
	onRemovePlayer: (index: 0 | 1, pid: number) => void;
	onClearRoster: (index: 0 | 1) => void;
	onChangePlayerSeason: (
		index: 0 | 1,
		pid: number,
		selectedSeason: number,
	) => void;
	defaultLeague?: ExhibitionLeagueWithSeasons;
	customTeamName1: string;
	customTeamName2: string;
	onChangeTeamName: (index: 0 | 1, name: string) => void;
	onSetCustomPlayers1: (players: Player[] | undefined) => void;
	onSetCustomPlayers2: (players: Player[] | undefined) => void;
}) => {
	const [leagueId, setLeagueId] = useState<string>("real");
	const [season, setSeason] = useState<
		number | "all" | { start: number; end: number }
	>("all");
	const [seasonInput, setSeasonInput] = useState<string>("");
	const [tid, setTid] = useState<number | "all">(0);
	const [teams, setTeams] = useState<ExhibitionTeam[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	type DraftCriteria = "mvp" | "ovr" | "mvp_dpoy";
	const [draftCriteria, setDraftCriteria] = useState<DraftCriteria>("mvp");

	const [leagueInfo, setLeagueInfo] = useState<
		ExhibitionLeagueWithSeasons | undefined
	>();

	useEffect(() => {
		const loadInitial = async () => {
			if (defaultLeague) {
				setLeagueId(
					defaultLeague.type === "real" ? "real" : String(defaultLeague.lid),
				);
			} else if (!REAL_PLAYERS_INFO && leagues.length > 0) {
				setLeagueId(String(leagues[0].lid));
			}
		};
		loadInitial();
	}, [leagues, defaultLeague]);

	useEffect(() => {
		const loadLeague = async () => {
			if (leagueId === "") return;
			let newLeague: ExhibitionLeagueWithSeasons;
			if (leagueId === "real") {
				newLeague = {
					type: "real",
					seasonStart: REAL_PLAYERS_INFO?.MIN_SEASON ?? 0,
					seasonEnd: REAL_PLAYERS_INFO?.MAX_SEASON ?? 0,
				};
			} else {
				const lid = Number.parseInt(leagueId);
				const { seasonStart, seasonEnd } = await toWorker(
					"exhibitionGame",
					"getSeasons",
					lid,
				);
				newLeague = {
					type: "league",
					lid,
					seasonStart,
					seasonEnd,
				};
			}
			setLeagueInfo(newLeague);
			setSeason(newLeague.seasonEnd);
			setSeasonInput(String(newLeague.seasonEnd));
		};
		loadLeague();
	}, [leagueId]);

	useEffect(() => {
		if (!leagueInfo) return;
		const loadTeams = async () => {
			setLoading(true);
			const pidOffset = 9 * 1e6; // Unique offset for draft browsing
			const newInfo = await toWorker(
				"exhibitionGame",
				"getSeasonInfo",
				leagueInfo.type === "real"
					? {
							type: "real",
							season,
							pidOffset,
						}
					: {
							type: "league",
							lid: leagueInfo.lid,
							season,
							pidOffset,
						},
			);
			const newTeams = orderBy(
				applyRealTeamInfos(
					newInfo.teams,
					realTeamInfo,
					typeof season === "object"
						? season.end
						: season === "all"
							? leagueInfo.seasonEnd
							: season,
				),
				["region", "name", "tid"],
			);
			setTeams(newTeams as any);
			if (newTeams.length > 0) {
				setTid(
					season === "all" || typeof season === "object"
						? "all"
						: newTeams[0].tid,
				);
			}
			setLoading(false);
		};
		loadTeams();
	}, [leagueInfo, season]);

	const handleSeasonInputBlur = () => {
		const cleaned = seasonInput.trim().toLowerCase();
		if (cleaned === "" || cleaned === "all") {
			setSeason("all");
			setSeasonInput("all");
			return;
		}
		if (cleaned.includes("-")) {
			const parts = cleaned.split("-").map((p) => Number.parseInt(p.trim()));
			if (!Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
				const start = Math.min(parts[0], parts[1]);
				const end = Math.max(parts[0], parts[1]);
				setSeason({ start, end });
				setSeasonInput(`${start}-${end}`);
				return;
			}
		}
		const val = Number.parseInt(cleaned);
		if (Number.isNaN(val)) {
			setSeason("all");
			setSeasonInput("all");
		} else {
			setSeason(val);
			setSeasonInput(String(val));
		}
	};

	const getPlayerPeakScore = (
		p: any,
		criteria: DraftCriteria = draftCriteria,
	) => {
		const r = p.ratings.at(-1)!;
		const s = p.stats.at(-1) || {};
		const ovr = r.ovr ?? 0;

		if (criteria === "ovr") {
			const ws = s.ws ?? 0;
			const vorp = s.vorp ?? 0;
			return ovr * 1000 + vorp * 10 + ws;
		}

		const mvpCount =
			p.awards?.filter(
				(a: any) =>
					a.type === "MVP" || a.type === "mvp" || a.type === "Most Valuable Player",
			).length ?? 0;
		const ws = s.ws ?? 0;
		const vorp = s.vorp ?? 0;
		const per = s.per ?? 0;

		if (criteria === "mvp_dpoy") {
			const dpoyCount =
				p.awards?.filter(
					(a: any) =>
						a.type === "DPOY" ||
						a.type === "dpoy" ||
						a.type === "Defensive Player of the Year",
				).length ?? 0;
			const dws = s.dws ?? 0;
			return (
				mvpCount * 100 +
				dpoyCount * 80 +
				ws * 5 +
				dws * 5 +
				vorp * 4 +
				per +
				ovr * 0.5
			);
		}

		return mvpCount * 100 + ws * 5 + vorp * 4 + per + ovr * 0.5;
	};

	const handleFillAllStars = () => {
		const allPlayers = teams.flatMap((t: any) =>
			t.players.map((p: any) => ({ ...p, cid: t.cid })),
		);
		const westPlayers = allPlayers
			.filter((p: any) => p.cid === 1)
			.sort((a: any, b: any) => getPlayerPeakScore(b) - getPlayerPeakScore(a))
			.slice(0, 15);
		const eastPlayers = allPlayers
			.filter((p: any) => p.cid === 0)
			.sort((a: any, b: any) => getPlayerPeakScore(b) - getPlayerPeakScore(a))
			.slice(0, 15);

		onSetCustomPlayers1(westPlayers);
		onSetCustomPlayers2(eastPlayers);
		onChangeTeamName(1, "WESTERN ALL STARS");
		onChangeTeamName(0, "EASTERN ALL STARS");
	};

	const handleAutoDraftBalanced = () => {
		const allPlayers = teams.flatMap((t) => t.players);
		const sorted = [...allPlayers].sort(
			(a, b) => getPlayerPeakScore(b) - getPlayerPeakScore(a),
		);
		const top30 = sorted.slice(0, 30);

		const roster1: Player[] = [];
		const roster2: Player[] = [];
		let sum1 = 0;
		let sum2 = 0;

		for (const p of top30) {
			const ovr = p.ratings.at(-1)!.ovr;
			if (roster1.length < 15 && (sum1 <= sum2 || roster2.length >= 15)) {
				roster1.push(p);
				sum1 += ovr;
			} else {
				roster2.push(p);
				sum2 += ovr;
			}
		}

		onSetCustomPlayers1(roster1);
		onSetCustomPlayers2(roster2);
		onChangeTeamName(1, "Balanced Team 1");
		onChangeTeamName(0, "Balanced Team 2");
	};

	const handlePutBestInTeam = (index: 0 | 1) => {
		const allPlayers = teams.flatMap((t) => t.players);
		const best15 = [...allPlayers]
			.sort((a, b) => getPlayerPeakScore(b) - getPlayerPeakScore(a))
			.slice(0, 15);

		if (index === 1) {
			onSetCustomPlayers1(best15);
			onChangeTeamName(1, "All-Stars T1");
		} else {
			onSetCustomPlayers2(best15);
			onChangeTeamName(0, "All-Stars T2");
		}
	};

	const handleWorldVsUsa = () => {
		const seenPids = new Set<number>();
		const allPlayers: Player[] = [];
		for (const t of teams) {
			for (const p of t.players) {
				if (!seenPids.has(p.pid)) {
					seenPids.add(p.pid);
					allPlayers.push(p);
				}
			}
		}

		const worldPlayers = allPlayers
			.filter((p: any) => p.born?.loc && !helpers.isAmerican(p.born.loc))
			.sort((a: any, b: any) => getPlayerPeakScore(b) - getPlayerPeakScore(a))
			.slice(0, 15);

		const usaPlayers = allPlayers
			.filter((p: any) => p.born?.loc && helpers.isAmerican(p.born.loc))
			.sort((a: any, b: any) => getPlayerPeakScore(b) - getPlayerPeakScore(a))
			.slice(0, 15);

		onSetCustomPlayers1(worldPlayers);
		onSetCustomPlayers2(usaPlayers);
		onChangeTeamName(1, "WORLD");
		onChangeTeamName(0, "USA");
	};

	const selectedTeam =
		tid === "all" ? undefined : teams.find((t) => t.tid === tid);
	const displayedPlayers = (
		tid === "all"
			? teams.flatMap((t) => t.players)
			: (selectedTeam?.players ?? [])
	).filter((p) =>
		`${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<Modal size="xl" show={show} onHide={onHide} scrollable>
			<Modal.Header closeButton>
				<Modal.Title className="h4">Custom Matchup Roster Builder</Modal.Title>
			</Modal.Header>
			<Modal.Body className="bg-light-subtle">
				<div className="row g-3" style={{ minHeight: "500px" }}>
					{/* Team 1 Roster */}
					<div className="col-12 col-lg-3">
						<div className="card h-100 shadow-sm border-0 bg-body">
							<div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pb-0 mt-2">
								<h5 className="mb-0 fw-bold">TEAM 1 Roster</h5>
								{customPlayers1 && customPlayers1.length > 0 && (
									<button
										className="btn btn-sm btn-outline-danger py-0 px-2"
										onClick={() => onClearRoster(1)}
									>
										Clear
									</button>
								)}
							</div>
							<div
								className="card-body pt-2"
								style={{ maxHeight: "400px", overflowY: "auto" }}
							>
								<div className="mb-2">
									<input
										type="text"
										className="form-control form-control-sm text-center fw-semibold text-primary bg-light-subtle border-0"
										style={{ fontSize: "0.85rem" }}
										placeholder="Rename TEAM 1"
										value={customTeamName1}
										onChange={(e) => onChangeTeamName(1, e.target.value)}
									/>
								</div>
								{!customPlayers1 || customPlayers1.length === 0 ? (
									<div className="text-center text-body-secondary py-5">
										<p className="mb-0 small">No custom players drafted.</p>
										<p className="text-muted small">
											Add players from the central pool!
										</p>
									</div>
								) : (
									<ul className="list-group list-group-flush">
										{customPlayers1.map((p, idx) => {
											const ratingsList = p.allRatings || p.ratings;
											const currentSeason = p.ratings.at(-1)!.season;
											return (
												<li
													key={`${p.pid}-${idx}`}
													className="list-group-item px-0 d-flex justify-content-between align-items-center bg-transparent border-bottom"
												>
													<div className="small">
														<div className="fw-bold">
															{currentSeason} {p.firstName} {p.lastName}
														</div>
														<div className="text-body-secondary">
															{p.ratings.at(-1)!.pos} - {p.ratings.at(-1)!.ovr}{" "}
															OVR
														</div>
														{ratingsList && ratingsList.length > 1 ? (
															<select
																className="form-select form-select-xs mt-1"
																style={{
																	fontSize: "0.75rem",
																	padding: "0.1rem 0.3rem",
																	width: "auto",
																}}
																value={currentSeason}
																onChange={(e) => {
																	const year = Number.parseInt(e.target.value);
																	onChangePlayerSeason(1, p.pid, year);
																}}
															>
																{ratingsList.map((r: any) => (
																	<option key={r.season} value={r.season}>
																		{r.season} ({r.ovr} OVR)
																	</option>
																))}
															</select>
														) : (
															<div
																className="text-body-secondary"
																style={{ fontSize: "0.75rem" }}
															>
																Season: {currentSeason}
															</div>
														)}
													</div>
													<button
														className="btn btn-xs btn-outline-danger border-0 p-1"
														onClick={() => onRemovePlayer(1, p.pid)}
														type="button"
														title="Remove Player"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															width="16"
															height="16"
															fill="currentColor"
															className="bi bi-trash"
															viewBox="0 0 16 16"
														>
															<path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
															<path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z" />
														</svg>
													</button>
												</li>
											);
										})}
									</ul>
								)}
							</div>
						</div>
					</div>

					{/* Draft Pool Selector */}
					<div className="col-12 col-lg-6">
						<div className="card h-100 shadow-sm border-0 bg-body">
							<div className="card-header bg-transparent border-0 pb-0 mt-2">
								<h5 className="mb-0 fw-bold">Browse saves & draft players</h5>
							</div>
							<div className="card-body">
								<div className="row g-2 mb-3">
									<div className="col-12 col-sm-6">
										<label className="form-label small mb-1">
											Save / League
										</label>
										<select
											className="form-select form-select-sm"
											value={leagueId}
											onChange={(e) => setLeagueId(e.target.value)}
										>
											{REAL_PLAYERS_INFO ? (
												<option value="real">Real historical teams</option>
											) : null}
											{leagues.map((l) => (
												<option key={l.lid} value={l.lid}>
													{l.name}
												</option>
											))}
										</select>
									</div>
									<div className="col-6 col-sm-3">
										<label className="form-label small mb-1">
											Season / Range
										</label>
										<input
											type="text"
											className="form-control form-control-sm"
											placeholder="e.g. 2002-2012, 2008, all"
											value={seasonInput}
											onChange={(e) => setSeasonInput(e.target.value)}
											onBlur={handleSeasonInputBlur}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													handleSeasonInputBlur();
												}
											}}
											disabled={!leagueInfo}
										/>
									</div>
									<div className="col-6 col-sm-3">
										<label className="form-label small mb-1">Team</label>
										<select
											className="form-select form-select-sm"
											value={teams.length === 0 ? "loading" : tid}
											onChange={(e) => {
												const val = e.target.value;
												if (val === "all") {
													setTid("all");
												} else {
													setTid(Number.parseInt(val));
												}
											}}
											disabled={loading || teams.length === 0}
										>
											{teams.length > 0 && (
												<option value="all">ALL TEAMS</option>
											)}
											{teams.map((t) => (
												<option key={t.tid} value={t.tid}>
													{t.region} {t.name}
												</option>
											))}
											{teams.length === 0 ? (
												<option value="loading">Loading...</option>
											) : null}
										</select>
									</div>
								</div>

								{/* Quick Draft Actions Row */}
								{teams.length > 0 && (
									<div className="mb-3">
										<div className="d-flex align-items-center gap-2 mb-2">
											<span className="small text-body-secondary fw-semibold">
												Draft Basis:
											</span>
											<div className="btn-group btn-group-xs" role="group">
												<button
													type="button"
													className={`btn btn-xs ${
														draftCriteria === "mvp"
															? "btn-primary"
															: "btn-outline-secondary"
													}`}
													onClick={() => setDraftCriteria("mvp")}
													disabled={loading}
												>
													MVP
												</button>
												<button
													type="button"
													className={`btn btn-xs ${
														draftCriteria === "ovr"
															? "btn-primary"
															: "btn-outline-secondary"
													}`}
													onClick={() => setDraftCriteria("ovr")}
													disabled={loading}
												>
													OVR
												</button>
												<button
													type="button"
													className={`btn btn-xs ${
														draftCriteria === "mvp_dpoy"
															? "btn-primary"
															: "btn-outline-secondary"
													}`}
													onClick={() => setDraftCriteria("mvp_dpoy")}
													disabled={loading}
												>
													MVP + DPOY
												</button>
											</div>
										</div>
										<div className="d-flex flex-wrap gap-2">
										<button
											type="button"
											className="btn btn-xs btn-outline-primary fw-semibold"
											onClick={handleFillAllStars}
											disabled={loading}
										>
											⭐ Fill All Stars
										</button>
										<button
											type="button"
											className="btn btn-xs btn-outline-success fw-semibold"
											onClick={handleAutoDraftBalanced}
											disabled={loading}
										>
											⚖️ Balanced Top 30
										</button>
										<button
											type="button"
											className="btn btn-xs btn-outline-info fw-semibold"
											onClick={handleWorldVsUsa}
											disabled={loading}
										>
											🌍 World vs USA
										</button>
										<button
											type="button"
											className="btn btn-xs btn-outline-secondary fw-semibold"
											onClick={() => handlePutBestInTeam(1)}
											disabled={loading}
										>
											➕ Best to T1
										</button>
										<button
											type="button"
											className="btn btn-xs btn-outline-secondary fw-semibold"
											onClick={() => handlePutBestInTeam(0)}
											disabled={loading}
										>
											➕ Best to T2
										</button>
									</div>
								</div>
								)}

								<div className="mb-3">
									<input
										type="text"
										className="form-control form-control-sm"
										placeholder="Search player name..."
										value={search}
										onChange={(e) => setSearch(e.target.value)}
									/>
								</div>

								<div style={{ maxHeight: "300px", overflowY: "auto" }}>
									{loading ? (
										<div className="text-center py-5">
											<div
												className="spinner-border spinner-border-sm text-primary"
												role="status"
											>
												<span className="visually-hidden">Loading...</span>
											</div>
										</div>
									) : displayedPlayers.length === 0 ? (
										<div className="text-center text-body-secondary py-5 small">
											No players match your search.
										</div>
									) : (
										<div className="list-group list-group-flush">
											{displayedPlayers.map((p) => {
												const rating = p.ratings.at(-1)!;
												return (
													<div
														key={p.pid}
														className="list-group-item px-0 d-flex justify-content-between align-items-center bg-transparent border-bottom py-2"
													>
														<div>
															<div className="fw-semibold small">
																{rating.season} {p.firstName} {p.lastName}
															</div>
															<div
																className="text-body-secondary"
																style={{ fontSize: "0.75rem" }}
															>
																{rating.pos} - {rating.ovr} OVR |{" "}
																{season === "all" || typeof season === "object"
																	? rating.season
																	: season - p.born.year}{" "}
																{season === "all" || typeof season === "object"
																	? "Season"
																	: "yrs"}
															</div>
														</div>
														<div className="btn-group btn-group-sm">
															<button
																className="btn btn-sm btn-light-bordered py-1"
																onClick={() => onAddPlayer(1, p)}
																type="button"
															>
																+ T1
															</button>
															<button
																className="btn btn-sm btn-light-bordered py-1"
																onClick={() => onAddPlayer(0, p)}
																type="button"
															>
																+ T2
															</button>
														</div>
													</div>
												);
											})}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Team 2 Roster */}
					<div className="col-12 col-lg-3">
						<div className="card h-100 shadow-sm border-0 bg-body">
							<div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pb-0 mt-2">
								<h5 className="mb-0 fw-bold">TEAM 2 Roster</h5>
								{customPlayers2 && customPlayers2.length > 0 && (
									<button
										className="btn btn-sm btn-outline-danger py-0 px-2"
										onClick={() => onClearRoster(0)}
									>
										Clear
									</button>
								)}
							</div>
							<div
								className="card-body pt-2"
								style={{ maxHeight: "400px", overflowY: "auto" }}
							>
								<div className="mb-2">
									<input
										type="text"
										className="form-control form-control-sm text-center fw-semibold text-danger bg-light-subtle border-0"
										style={{ fontSize: "0.85rem" }}
										placeholder="Rename TEAM 2"
										value={customTeamName2}
										onChange={(e) => onChangeTeamName(0, e.target.value)}
									/>
								</div>
								{!customPlayers2 || customPlayers2.length === 0 ? (
									<div className="text-center text-body-secondary py-5">
										<p className="mb-0 small">No custom players drafted.</p>
										<p className="text-muted small">
											Add players from the central pool!
										</p>
									</div>
								) : (
									<ul className="list-group list-group-flush">
										{customPlayers2.map((p, idx) => {
											const ratingsList = p.allRatings || p.ratings;
											const currentSeason = p.ratings.at(-1)!.season;
											return (
												<li
													key={`${p.pid}-${idx}`}
													className="list-group-item px-0 d-flex justify-content-between align-items-center bg-transparent border-bottom"
												>
													<div className="small">
														<div className="fw-bold">
															{currentSeason} {p.firstName} {p.lastName}
														</div>
														<div className="text-body-secondary">
															{p.ratings.at(-1)!.pos} - {p.ratings.at(-1)!.ovr}{" "}
															OVR
														</div>
														{ratingsList && ratingsList.length > 1 ? (
															<select
																className="form-select form-select-xs mt-1"
																style={{
																	fontSize: "0.75rem",
																	padding: "0.1rem 0.3rem",
																	width: "auto",
																}}
																value={currentSeason}
																onChange={(e) => {
																	const year = Number.parseInt(e.target.value);
																	onChangePlayerSeason(0, p.pid, year);
																}}
															>
																{ratingsList.map((r: any) => (
																	<option key={r.season} value={r.season}>
																		{r.season} ({r.ovr} OVR)
																	</option>
																))}
															</select>
														) : (
															<div
																className="text-body-secondary"
																style={{ fontSize: "0.75rem" }}
															>
																Season: {currentSeason}
															</div>
														)}
													</div>
													<button
														className="btn btn-xs btn-outline-danger border-0 p-1"
														onClick={() => onRemovePlayer(0, p.pid)}
														type="button"
														title="Remove Player"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															width="16"
															height="16"
															fill="currentColor"
															className="bi bi-trash"
															viewBox="0 0 16 16"
														>
															<path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
															<path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z" />
														</svg>
													</button>
												</li>
											);
										})}
									</ul>
								)}
							</div>
						</div>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button className="btn btn-primary" onClick={onHide}>
					Done drafting
				</button>
			</Modal.Footer>
		</Modal>
	);
};

const Exhibition = ({ defaultSettings, realTeamInfo }: View<"exhibition">) => {
	// Default state comes from cache of last exhibition game, if possible
	const defaultState = useMemo(() => {
		let settings: CachedSettings | undefined;
		try {
			const json = safeLocalStorage.getItem(CACHE_KEY);
			if (json) {
				settings = JSON.parse(json) as CachedSettings;
			}
		} catch {}

		if (settings) {
			// Add default, in case there is a new setting
			if (settings.gameAttributesInfo.type === "custom") {
				settings.gameAttributesInfo.custom = {
					...defaultSettings,
					...settings.gameAttributesInfo.custom,
				};
			}
			return settings;
		}

		return {
			gameAttributesInfo: {
				type: "t1",
			} as GameAttributesInfo,
			neutralSite: true,
			playoffIntensity: true,
			swapHomeAway: false,
			teams: undefined,
			customPlayers1: undefined,
			customPlayers2: undefined,
			customTeamName1: "TEAM 1",
			customTeamName2: "TEAM 2",
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [numPlayersOnCourt, setNumPlayersOnCourt] = useState(5);
	const [customPlayers1, setCustomPlayers1] = useState<Player[] | undefined>(
		defaultState.customPlayers1,
	);
	const [customPlayers2, setCustomPlayers2] = useState<Player[] | undefined>(
		defaultState.customPlayers2,
	);
	const [customTeamName1, setCustomTeamName1] = useState<string>(
		defaultState.customTeamName1 && defaultState.customTeamName1 !== "Team 1"
			? defaultState.customTeamName1
			: "TEAM 1",
	);
	const [customTeamName2, setCustomTeamName2] = useState<string>(
		defaultState.customTeamName2 && defaultState.customTeamName2 !== "Team 2"
			? defaultState.customTeamName2
			: "TEAM 2",
	);
	const [showDraftModal, setShowDraftModal] = useState(false);

	const onChangeTeamName = (index: 0 | 1, name: string) => {
		if (index === 1) {
			setCustomTeamName1(name);
		} else {
			setCustomTeamName2(name);
		}
	};

	const handleAddPlayer = (index: 0 | 1, player: Player) => {
		if (index === 1) {
			setCustomPlayers1((prev) => [...(prev ?? []), player]);
		} else {
			setCustomPlayers2((prev) => [...(prev ?? []), player]);
		}
	};

	const handleRemovePlayer = (index: 0 | 1, pid: number) => {
		if (index === 1) {
			setCustomPlayers1((prev) => prev?.filter((p) => p.pid !== pid));
		} else {
			setCustomPlayers2((prev) => prev?.filter((p) => p.pid !== pid));
		}
	};

	const handleClearRoster = (index: 0 | 1) => {
		if (index === 1) {
			setCustomPlayers1([]);
		} else {
			setCustomPlayers2([]);
		}
	};

	const handleChangePlayerSeason = (
		index: 0 | 1,
		pid: number,
		selectedSeason: number,
	) => {
		const updatePlayer = (prev: Player[] | undefined) => {
			if (!prev) return prev;
			return prev.map((p) => {
				if (p.pid !== pid) return p;

				const ratings =
					p.allRatings?.find((r: any) => r.season === selectedSeason) ??
					p.ratings[0];
				const stats =
					p.allStats?.find(
						(s: any) => s.season === selectedSeason && !s.playoffs,
					) ??
					p.allStats?.find((s: any) => s.season === selectedSeason) ??
					p.stats[0];

				return {
					...p,
					ratings: [ratings],
					stats: [stats],
				};
			});
		};

		if (index === 1) {
			setCustomPlayers1(updatePlayer);
		} else {
			setCustomPlayers2(updatePlayer);
		}
	};

	const leagues = useLeagues();
	const [teams, setTeams] = useState<
		[
			ExhibitionTeamAndSettings | undefined,
			ExhibitionTeamAndSettings | undefined,
		]
	>([undefined, undefined]);
	const [neutralSite, setNeutralSite] = useState(defaultState.neutralSite);
	const [swapHomeAway, setSwapHomeAway] = useState(
		defaultState.swapHomeAway ?? false,
	);
	const [playoffIntensity, setPlayoffIntensity] = useState(
		defaultState.playoffIntensity,
	);
	const [simmingGame, setSimmingGame] = useState(false);
	const [gameAttributesInfo, setGameAttributesInfo] =
		useState<GameAttributesInfo>(defaultState.gameAttributesInfo);
	const [showCustomizeModal, setShowCustomizeModal] = useState(false);

	const loadingTeams = teams[0] === undefined || teams[1] === undefined;

	useTitleBar({
		title: "Exhibition Game",
		hideNewWindow: true,
	});

	const setTeam = (
		index: 0 | 1,
		league: ExhibitionLeagueWithSeasons,
		t: ExhibitionTeam | undefined,
		gameAttributes: ExhibitionGameAttributes,
	) => {
		setTeams((teams) => {
			let newTeams: typeof teams;
			const entry = t === undefined ? undefined : { gameAttributes, league, t };
			if (index === 0) {
				newTeams = [entry, teams[1]];
			} else {
				newTeams = [teams[0], entry];
			}

			return newTeams;
		});
	};

	const getGameAttributesByType = () => {
		let gameAttributes;
		if (gameAttributesInfo.type === "custom") {
			gameAttributes = gameAttributesInfo.custom;
		} else if (gameAttributesInfo.type === "t0") {
			gameAttributes = getGameAttributes(teams[0]?.gameAttributes);
		} else if (gameAttributesInfo.type === "t1") {
			gameAttributes = getGameAttributes(teams[1]?.gameAttributes);
		} else {
			gameAttributes = getGameAttributes();
		}
		return gameAttributes;
	};

	const onHideCustomizeModal = () => {
		setShowCustomizeModal(false);
	};

	const gameSimSettingsText = (
		entry: ExhibitionTeamAndSettings | undefined,
	) => {
		if (!entry) {
			return "Loading...";
		}

		const league = entry.league;
		if (league.type === "real") {
			return entry.t.season;
		}

		return (
			leagues?.find((league2) => league2.lid === league.lid)?.name ?? "???"
		);
	};

	let gameAttributesWarning;
	if (gameAttributesInfo.type === "t0" || gameAttributesInfo.type === "t1") {
		const entry = teams[gameAttributesInfo.type === "t0" ? 0 : 1];
		const league = entry?.league;
		if (league && league.type !== "real") {
			const league2 = leagues?.find((league2) => league2.lid === league.lid);
			if (league2 && entry.t.season !== league.seasonEnd) {
				gameAttributesWarning = (
					<div className="text-danger mb-2">
						Past season game sim settings are currently not saved, so the latest
						settings in {league2.name} will be used.
					</div>
				);
			}
		}
	}

	const leaguesDefined = useMemo(() => leagues ?? [], [leagues]);

	if (!REAL_PLAYERS_INFO && leagues?.length === 0) {
		return (
			<p>
				You need to <a href="/new_league">create some leagues</a> before you
				play an exhibition game.
			</p>
		);
	}

	return (
		<div className="d-lg-flex">
			<div
				className="row gx-5 mb-4 mb-lg-0 me-lg-4 flex-shrink-0"
				style={{ maxWidth: 700, width: "100%" }}
			>
				<div className="col-12 col-sm-6">
					<h2>{neutralSite ? "TEAM 1" : swapHomeAway ? "Home" : "Away"}</h2>
					<SelectTeam
						disabled={simmingGame}
						index={1}
						initialTeam={defaultState.teams?.[1]}
						leagues={leaguesDefined}
						realTeamInfo={realTeamInfo}
						customPlayers={customPlayers1}
						customTeamName={customTeamName1}
						onOpenDraft={() => setShowDraftModal(true)}
						onReset={() => setCustomPlayers1(undefined)}
						onChange={(league, t, gameAttributes) => {
							setTeam(1, league, t, gameAttributes);
						}}
					/>
				</div>
				<div className="col-12 col-sm-6 mt-3 mt-sm-0">
					<h2>{neutralSite ? "TEAM 2" : swapHomeAway ? "Away" : "Home"}</h2>
					<SelectTeam
						disabled={simmingGame}
						index={0}
						initialTeam={defaultState.teams?.[0]}
						leagues={leaguesDefined}
						realTeamInfo={realTeamInfo}
						customPlayers={customPlayers2}
						customTeamName={customTeamName2}
						onOpenDraft={() => setShowDraftModal(true)}
						onReset={() => setCustomPlayers2(undefined)}
						onChange={(league, t, gameAttributes) => {
							setTeam(0, league, t, gameAttributes);
						}}
					/>
				</div>
			</div>

			<form
				onSubmit={async (event) => {
					event.preventDefault();

					setSimmingGame(true);

					const baseGameAttributes = getGameAttributesByType();
					const gameAttributes = {
						...baseGameAttributes,
						numPlayersOnCourt,
					};

					const toSave: CachedSettings = {
						gameAttributesInfo,
						neutralSite,
						playoffIntensity,
						swapHomeAway: neutralSite ? undefined : swapHomeAway,
						teams: teams.map((entry) => {
							if (!entry) {
								throw new Error("Missing entry");
							}

							if (entry.league.type === "real") {
								return {
									type: "real",
									season: entry.t.season,
									tid: entry.t.tid,
								};
							}

							return {
								type: "league",
								lid: entry.league.lid,
								season: entry.t.season,
								tid: entry.t.tid,
							};
						}) as [CachedTeam, CachedTeam],
						customPlayers1,
						customPlayers2,
						customTeamName1,
						customTeamName2,
					};
					safeLocalStorage.setItem(CACHE_KEY, JSON.stringify(toSave));

					// Keep drafted players outside every historical/source pid offset and
					// assign one id globally across both custom rosters.
					let customPid = 10_000_000;
					const simTeams = teams.map((entry, index) => {
						if (!entry) {
							throw new Error("Missing entry");
						}

						const teamCopy = { ...entry.t };
						const customPlayers = index === 0 ? customPlayers2 : customPlayers1;

						if (customPlayers && customPlayers.length > 0) {
							teamCopy.players = customPlayers.map((p, idx) => {
								const uniquePlayer = {
									...p,
									pid: customPid,
									rosterOrder: idx,
								};
								customPid += 1;
								return uniquePlayer;
							});
							teamCopy.depth = undefined;
							delete (teamCopy as any).season;
							delete (teamCopy as any).seasonInfo;
							(teamCopy as any).custom = true;

							if (index === 1) {
								teamCopy.region = "";
								teamCopy.name = customTeamName1 || "TEAM 1";
								teamCopy.abbrev = customTeamName1 ? customTeamName1.slice(0, 3).toUpperCase() : "TM1";
							} else if (index === 0) {
								teamCopy.region = "";
								teamCopy.name = customTeamName2 || "TEAM 2";
								teamCopy.abbrev = customTeamName2 ? customTeamName2.slice(0, 3).toUpperCase() : "TM2";
							}
						}

						return teamCopy;
					}) as [ExhibitionTeam, ExhibitionTeam];

					if (swapHomeAway && !neutralSite) {
						simTeams.reverse();
					}

					await toWorker("exhibitionGame", "simExhibitionGame", {
						neutralSite,
						gameAttributes,
						phase: playoffIntensity ? PHASE.PLAYOFFS : PHASE.REGULAR_SEASON,
						teams: simTeams,
					});
				}}
			>
				<div className="mb-4">
					<h2 className="form-label h2 mb-2">Matchup Type</h2>
					<div className="btn-group w-100" role="group">
						{[
							{ id: "1v1", label: "1v1 Duel", num: 1 },
							{ id: "2v2", label: "2v2 Duos", num: 2 },
							{ id: "3v3", label: "3v3 streetball", num: 3 },
							{ id: "5v5", label: "5v5 (Standard)", num: 5 },
						].map((opt) => (
							<button
								key={opt.id}
								type="button"
								className={`btn btn-sm ${
									numPlayersOnCourt === opt.num
										? "btn-primary"
										: "btn-light-bordered"
								}`}
								onClick={() => {
									setNumPlayersOnCourt(opt.num);
								}}
								disabled={simmingGame}
							>
								{opt.label}
							</button>
						))}
					</div>
				</div>

				<div className="mb-2" style={{ width: 200 }}>
					<label htmlFor="gameAttributesSelect" className="form-label h2">
						Game Sim Settings
					</label>
					<div className="input-group">
						<select
							id="gameAttributesSelect"
							className="form-select"
							value={gameAttributesInfo.type}
							onChange={async (event) => {
								const type = event.target.value;

								if (type === "custom") {
									throw new Error("Should never happen");
								} else {
									setGameAttributesInfo({
										type: type as any,
									});
								}
							}}
							disabled={simmingGame || loadingTeams}
						>
							<option value="t1">{gameSimSettingsText(teams[1])}</option>
							{!teams[0] || teams[0].t.season !== teams[1]?.t.season ? (
								<option value="t0">{gameSimSettingsText(teams[0])}</option>
							) : null}
							<option value="default">Default</option>
							{gameAttributesInfo.type === "custom" ? (
								<option value="custom">Custom</option>
							) : null}
						</select>
						<button
							className="btn btn-light-bordered"
							type="button"
							disabled={simmingGame || loadingTeams}
							onClick={() => {
								const custom = getGameAttributesByType();
								setGameAttributesInfo({
									type: "custom",
									custom,
								});
								setShowCustomizeModal(true);
							}}
						>
							Customize
						</button>
					</div>
				</div>
				{gameAttributesWarning}

				<div className="d-flex mb-3">
					<div>
						<div className="form-check mb-2">
							<input
								className="form-check-input"
								type="checkbox"
								id="neutralSiteCheck"
								checked={neutralSite}
								disabled={simmingGame}
								onChange={() => {
									setNeutralSite(!neutralSite);
								}}
							/>
							<label className="form-check-label" htmlFor="neutralSiteCheck">
								Neutral {COURT}
							</label>
							{neutralSite ? (
								<span className="badge bg-secondary-subtle text-secondary border ms-2">
									Neutral Court
								</span>
							) : null}
						</div>

						{__SPORT === "basketball" ? (
							<div className="form-check">
								<input
									className="form-check-input"
									type="checkbox"
									id="playoffIntensityCheck"
									checked={playoffIntensity}
									disabled={simmingGame}
									onChange={() => {
										setPlayoffIntensity(!playoffIntensity);
									}}
								/>
								<label
									className="form-check-label"
									htmlFor="playoffIntensityCheck"
								>
									Playoff intensity
								</label>
							</div>
						) : null}
						<button
							className="btn btn-light-bordered btn-sm mt-2"
							type="button"
							disabled={loadingTeams || simmingGame}
							onClick={async () => {
								const simTeams = teams.map((entry, index) => {
									if (!entry) return undefined;
									const teamCopy = { ...entry.t };
									const customPlayers =
										index === 1 ? customPlayers1 : customPlayers2;

									if (customPlayers && customPlayers.length > 0) {
										teamCopy.players = customPlayers;
										delete (teamCopy as any).season;
										delete (teamCopy as any).seasonInfo;
										(teamCopy as any).custom = true;
										if (index === 1) {
											teamCopy.region = "";
											teamCopy.name = customTeamName1 || "TEAM 1";
											teamCopy.abbrev = customTeamName1 ? customTeamName1.slice(0, 3).toUpperCase() : "TM1";
										} else if (index === 0) {
											teamCopy.region = "";
											teamCopy.name = customTeamName2 || "TEAM 2";
											teamCopy.abbrev = customTeamName2 ? customTeamName2.slice(0, 3).toUpperCase() : "TM2";
										}
									}
									return teamCopy;
								});

								const summary = await toWorker(
									"main",
									"generateExhibitionMatchupSummary",
									{
										teams: simTeams.filter(Boolean),
										neutralSite,
										playoffIntensity,
									},
								);
								if (summary) await navigator.clipboard.writeText(summary);
							}}
							title="Copy matchup summary to clipboard"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="14"
								fill="currentColor"
								viewBox="0 0 16 16"
							>
								<path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z" />
								<path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z" />
							</svg>
							<span className="ms-1">Copy Matchup</span>
						</button>
					</div>

					{!neutralSite ? (
						<button
							className="btn py-1 btn-light-bordered ms-2"
							type="button"
							onClick={() => {
								setSwapHomeAway((swap) => !swap);
							}}
						>
							Swap
							<br />
							home/away
						</button>
					) : null}
				</div>

				<ActionButton
					disabled={loadingTeams}
					processing={simmingGame}
					processingText="Simming"
					size="lg"
					type="submit"
				>
					Sim Game
				</ActionButton>
			</form>

			<RosterDraftModal
				show={showDraftModal}
				onHide={() => setShowDraftModal(false)}
				leagues={leaguesDefined}
				realTeamInfo={realTeamInfo}
				onAddPlayer={handleAddPlayer}
				customPlayers1={customPlayers1}
				customPlayers2={customPlayers2}
				onRemovePlayer={handleRemovePlayer}
				onClearRoster={handleClearRoster}
				onChangePlayerSeason={handleChangePlayerSeason}
				defaultLeague={teams[1]?.league ?? teams[0]?.league}
				customTeamName1={customTeamName1}
				customTeamName2={customTeamName2}
				onChangeTeamName={onChangeTeamName}
				onSetCustomPlayers1={setCustomPlayers1}
				onSetCustomPlayers2={setCustomPlayers2}
			/>

			<Modal
				size="xl"
				show={showCustomizeModal}
				onHide={onHideCustomizeModal}
				scrollable
			>
				<Modal.Body>
					<SettingsForm
						onSave={(settings) => {
							setGameAttributesInfo({
								type: "custom",
								// getGameAttributes call should not be necessary, but let's just be save
								custom: getGameAttributes(settings as ExhibitionGameAttributes),
							});
							onHideCustomizeModal();
						}}
						onCancel={onHideCustomizeModal}
						initialSettings={{
							...defaultSettings,
							...getGameAttributesByType(),
							godMode: true,
						}}
						settingsShown={EXHIBITION_GAME_SETTINGS}
						hideShortcuts
						isInsideModal
						hideGodModeToggle
					/>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default Exhibition;
